import { useEffect } from 'react';
import { CardModalFormNames, CardModalFormValues } from '../types/CardModalFormNames';
import { getFleetLimiterCodesObj } from 'features/cards/helpers/add-edit-card-helper';

import { getAddEditCardRequest } from 'features/cards/helpers/add-edit-card-helper';
import { createCard } from 'actions/cardActions';

import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone.js';
import utc from 'dayjs/plugin/utc';
import Vas from 'constants/vas-codes';
import {
  useSelectAccountDefaults,
  useSelectAccountDefaultsLoading,
  useSelectAssignedGrandfatherClientNumber,
  useSelectPlasticType,
} from 'selectors/selectors';
import {
  useDispatchCardFormValues,
  useDispatchCardModalMeta,
  useDispatchGetPlasticType,
} from 'dispatchers/dispatchers';
import { useDispatch, useSelector } from 'react-redux';
import { trackCustomEvent } from 'utilities/app-insights';
import { useGetDefaultAccount } from '../useGetDefaultAccount';
import { UserState } from 'types/UserState';

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * This hook handles `Add Card` specific logic for the CardModal.
 */
export default function useAddCardFormController(onClose: () => void) {
  const dispatch = useDispatch();
  const dispatchCardFormValues = useDispatchCardFormValues();
  const dispatchGetPlasticType = useDispatchGetPlasticType();
  const dispatchCardModalMeta = useDispatchCardModalMeta();

  const accountDefaults = useSelectAccountDefaults();
  const plasticType = useSelectPlasticType();
  const { defaultAccount } = useGetDefaultAccount();
  const assignedGrandfatherClientNumber = useSelectAssignedGrandfatherClientNumber();
  const isLoading = useSelectAccountDefaultsLoading();

  const userStore: UserState = useSelector((state: any) => state.userStore);
  const loggedInUser = userStore.loggedInUser;
  const assignedAccountNumber = userStore.assignedAccountNumber;

  // Autofill the form with the account defaults when they are loaded.
  useEffect(() => {
    if (!accountDefaults) return;
    if (Object.keys(accountDefaults).length === 0) return;
    const baseValues = {
      ...accountDefaults,
      ...getFleetLimiterCodesObj(accountDefaults.fleetLimiters),
      [CardModalFormNames.AllProducts]: accountDefaults.fleetLimiters?.every(
        (item) => item.available === false || item.selected === true
      ),
      [CardModalFormNames.FuelPay]:
        accountDefaults.vasFuelPayAccountCode !== Vas.FP_YA && accountDefaults.vasFuelPayAccountCode !== Vas.FP_NC,
    };

    const values =
      accountDefaults['verifyPosPrompt'] === 'Mandatory PIN'
        ? {
            ...baseValues,
            [CardModalFormNames.VerificationMethod]: 'Mandatory PIN',
          }
        : baseValues;
    dispatchCardFormValues(values);
    dispatchCardModalMeta({
      dailyDollarLimits: accountDefaults.dailyDollarLimits,
      monthlyDollarLimits: accountDefaults.monthlyDollarLimits,
      fleetLimiters: accountDefaults.fleetLimiters,
      vasRsaAccountCode: accountDefaults.vasRsaAccountCode,
      vasSnrAccountCode: accountDefaults.vasSnrAccountCode,
      disableEmbossingName: accountDefaults?.embossedLastName != undefined,
      verifyPosPrompt: accountDefaults?.verifyPosPrompt,
    });

    if (plasticType?.id !== accountDefaults?.cardInfo?.plasticCardName)
      dispatchGetPlasticType(accountDefaults?.cardInfo?.plasticCardName);
  }, [accountDefaults]);

  useEffect(() => {
    dispatchCardFormValues({
      [CardModalFormNames.Account]: defaultAccount,
      [CardModalFormNames.IssueDateFrom]: dayjs().format('DD/MM/YYYY'),
      [CardModalFormNames.IssueDateTo]: dayjs().add(60, 'month').tz('Australia/Sydney').format('MM/YY'),
    });
  }, []);

  function onFormSubmit(values: CardModalFormValues) {
    const request = getAddEditCardRequest(values, accountDefaults?.fleetLimiters, assignedGrandfatherClientNumber);
    dispatch(createCard(request));
    trackCustomEvent(
      'Submit Add New Card',
      loggedInUser,
      assignedAccountNumber,
      'Clicked Submit to submit Add New Card.',
      { selectedAccount: request?.contractNumber }
    );
    onClose();
  }

  return {
    onFormSubmit,
    isLoading,
  };
}
